import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  IChatSessionData,
  IConversationHistory,
  IQuerySingleQuestion,
  ISingleConversationHistoryItem,
  TQueriesHistory,
} from "@/types/(my-space)/ai-assistant.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyAiAssistantApi = createApi({
  reducerPath: "companyAiAssistantApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["CompanyGetAllConversations", "CompanyGetAllQueries", "CompanyGetSingleConversation", "CompanyAIShareLink"],
  endpoints: (builder) => ({
    getSingleConversation: builder.query<
      IApiPaginatedResponse<IConversationHistory>,
      { sessionId: string; messageId?: number; page?: number; limit?: number; latest?: boolean }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.AIAssistant.GetSingleConversationHistory,
        params,
      }),
      providesTags: ["CompanyGetSingleConversation"],
    }),
    getConversationHistory: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleConversationHistoryItem[]>,
      { limit?: number; search: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.Company.AIAssistant.GetConversations,
        params: { page: pageParam, limit: queryArg.limit, search: queryArg.search },
      }),
      providesTags: ["CompanyGetAllConversations"],
    }),
    getQueries: builder.infiniteQuery<
      IApiPaginatedResponse<TQueriesHistory[]>,
      { limit?: number; search: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.Company.AIAssistant.GetConversationsWithQuestions,
        params: { page: pageParam, limit: queryArg.limit, search: queryArg.search },
      }),
      providesTags: ["CompanyGetAllQueries"],
    }),
    getQuestionsBySessionId: builder.infiniteQuery<
      IApiPaginatedResponse<IQuerySingleQuestion[]>,
      { sessionId?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.Company.AIAssistant.GetQuestionsBySessionId,
        params: { page: pageParam, sessionId: queryArg.sessionId },
      }),
    }),
    pinConversation: builder.mutation<IApiResponse<void>, { sessionId?: string; pinned: boolean }>({
      query: (body) => ({
        url: APIEndpoints.Company.AIAssistant.PinConversation,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyGetAllConversations"],
    }),
    renameConversation: builder.mutation<IApiResponse<void>, { sessionId?: string; newTitle: string }>({
      query: (body) => ({
        url: APIEndpoints.Company.AIAssistant.RenameConversation,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CompanyGetAllConversations"],
    }),
    editMessage: builder.mutation<IApiResponse<IChatSessionData>, { messageId: number; content: string }>({
      query: (body) => ({
        url: APIEndpoints.Company.AIAssistant.EditMessage,
        method: "PATCH",
        body,
      }),
    }),
    deleteConversation: builder.mutation<IApiResponse<void>, { conversationIds: number[] }>({
      query: (body) => ({
        url: APIEndpoints.Company.AIAssistant.DeleteConversation,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["CompanyGetAllConversations"],
    }),
    speechToText: builder.mutation<IApiResponse<{ output: string }>, FormData>({
      query: (body) => ({
        url: APIEndpoints.Company.AIAssistant.SpeechToText,
        method: "POST",
        body,
      }),
    }),
    getShareLink: builder.query<
      IApiResponse<{
        token: string;
        url: string;
        isActive: boolean;
        expiresAt: string | null;
        snapshotMessageId: number | null;
        sessionId: string;
        viewCount: number;
      } | null>,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: APIEndpoints.Company.AIAssistant.ShareConversation(sessionId),
      }),
      providesTags: (_r, _e, arg) => [{ type: "CompanyAIShareLink", id: arg.sessionId }],
    }),
    createShareLink: builder.mutation<
      IApiResponse<{
        token: string;
        url: string;
        isActive: boolean;
        expiresAt: string | null;
        snapshotMessageId: number | null;
        sessionId: string;
      }>,
      { sessionId: string; expiresInDays?: number }
    >({
      query: ({ sessionId, expiresInDays }) => ({
        url: APIEndpoints.Company.AIAssistant.ShareConversation(sessionId),
        method: "POST",
        body: expiresInDays != null ? { expiresInDays } : {},
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "CompanyAIShareLink", id: arg.sessionId }],
    }),
    revokeShareLink: builder.mutation<IApiResponse<null>, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: APIEndpoints.Company.AIAssistant.ShareConversation(sessionId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "CompanyAIShareLink", id: arg.sessionId }],
    }),
  }),
});

export const {
  useGetConversationHistoryInfiniteQuery,
  useGetQueriesInfiniteQuery,
  usePinConversationMutation,
  useRenameConversationMutation,
  useDeleteConversationMutation,
  useLazyGetSingleConversationQuery,
  useEditMessageMutation,
  useGetQuestionsBySessionIdInfiniteQuery,
  useSpeechToTextMutation,
  useGetShareLinkQuery,
  useLazyGetShareLinkQuery,
  useCreateShareLinkMutation,
  useRevokeShareLinkMutation,
} = companyAiAssistantApi;
